import { useMemo, useState, useEffect, useRef } from "react";
import { HiOutlineClipboardList } from "react-icons/hi";
import { FaCheckCircle, FaQuestionCircle, FaStar } from "react-icons/fa";

/**
 * CardQueryDescription — VARIANTE "SIDE ADD-ONS" (REFEITO)
 * - Cards de mesma altura (h-[68vh]) com scroll interno
 * - Form à esquerda; Adicionais à direita (faixas compactas)
 * - Detalhes do produto (Descrição + O que retorna) unificados e mais visuais
 * - Quando NÃO houver adicionais e o painel de detalhes tiver overflow, mostra uma dica animada de scroll
 */
export default function CardQueryDescription({
  title,
  subtitle = "",
  infos = [],
  description = "",
  additionalOptions = [],
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
  logoOverride,
  kicker = "Consulta de crédito",
  children,
  tags = [],
}) {
  const hasAdds =
    Array.isArray(additionalOptions) && additionalOptions.length > 0;
  const hasDescription = Boolean((description || "").trim());
  const hasTags = Array.isArray(tags) && tags.length > 0;

  const selectedSet = useMemo(
    () => new Set((selectedAdditionalIds || []).map(String)),
    [selectedAdditionalIds]
  );
  const selectedCount = selectedSet.size;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg pb-5">
      {/* Header */}
      <header className="px-5 py-4 md:px-8 md:py-5 border-b border-slate-200 bg-gray-50">
        <div className="flex flex-col gap-1 md:gap-0">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <h1 className="text-xl md:text-2xl font-extrabold leading-tight tracking-tight text-slate-900">
              {title}
            </h1>
            {kicker && (
              <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-full">
                {kicker}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs md:text-sm text-slate-600">{subtitle}</p>
          )}
        </div>
      </header>

      {/* Top content: Form (left) + Add-ons (right ou Detalhes quando não há add-ons) */}
      <div className="px-3 md:px-8 pt-5 pb-2">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          {/* Form */}
          <section className="lg:col-span-6">
            <Block
              title="Consultar"
              icon={<HiOutlineClipboardList className="h-4 w-4" />}
              className="md:h-[255px] flex flex-col rounded-3xl border border-indigo-100 ring-1 ring-slate-900/5 bg-gradient-to-b from-indigo-50/70 to-fuchsia-50/20"
            >
              {/* Conteúdo scrollável do form */}
              <div className="min-h-0 flex-1 p-5 md:p-6">
                <div className="space-y-3">{children}</div>
              </div>

              {/* Rodapé fixo */}
              <div className="shrink-0 border-t border-slate-200 px-5 md:px-6 py-3">
                <p className="text-xs text-slate-500">
                  Dica: valide o documento e, se desejar, inclua módulos
                  adicionais para enriquecer o relatório.
                </p>
              </div>
            </Block>
          </section>

          {/* Add-ons OU Detalhes do produto (Descrição + Tags) quando não houver add-ons */}
          <aside className="lg:col-span-6 hidden md:block">
            {hasAdds ? (
              <CompactAddonsPanel
                additionalOptions={additionalOptions}
                selectedSet={selectedSet}
                selectedAdditionalIds={selectedAdditionalIds}
                onChangeAdditionalIds={onChangeAdditionalIds}
                selectedCount={selectedCount}
              />
            ) : hasDescription || hasTags ? (
              <ProductDetailsPanel
                description={description}
                tags={tags}
                compact
                heightClass="h-[255px]"
                showScrollHint // ativa a dica de scroll quando overflow
              />
            ) : null}
          </aside>
        </div>
      </div>

      {/* Bottom content: Detalhes do produto unificado (só aparece quando EXISTEM add-ons) */}
      {hasAdds && (hasDescription || hasTags) && (
        <div className="px-6 md:px-8 pb-6">
          <ProductDetailsPanel description={description} tags={tags} />
        </div>
      )}
    </section>
  );
}

/* --------------------------------- Helpers -------------------------------- */

function Block({ title, icon, children, className = "" }) {
  return (
    <section className={`rounded-2xl ${className}`}>
      {title && (
        <div className="px-5 md:px-6 pt-4 pb-3 flex items-center gap-2 border-b border-transparent">
          {icon && <span className="text-slate-500">{icon}</span>}
          <h2 className="text-[11px] uppercase tracking-[0.18em] text-slate-600 font-semibold">
            {title}
          </h2>
        </div>
      )}
      {children}
    </section>
  );
}

/* ===== Painel unificado: Descrição + O que retorna (Tags) ===== */
function ProductDetailsPanel({
  description = "",
  tags = [],
  compact = false,
  heightClass = "",
  showScrollHint = false,
}) {
  const hasDescription = Boolean((description || "").trim());
  const hasTags = Array.isArray(tags) && tags.length > 0;

  // ---- Scroll hint (mostra quando o conteúdo extrapola) ----
  const scrollRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [atBottom, setAtBottom] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const check = () => {
      const overflow = el.scrollHeight > el.clientHeight + 2; // tolerância
      setIsOverflowing(overflow);
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
      // se houver overflow e a dica estiver habilitada, mostra por padrão
      setHintVisible(overflow && showScrollHint);
    };

    check();

    // Observa resize/alterações de layout
    const ro = new ResizeObserver(check);
    ro.observe(el);

    // Observa mudanças de conteúdo (caso description/tags mudem dinamicamente)
    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    const onScroll = () => {
      // ao rolar, esmaece a dica; volta a aparecer se voltar ao topo
      const atTop = el.scrollTop <= 8;
      const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setAtBottom(isBottom);
      setHintVisible(showScrollHint && isOverflowing && atTop && !isBottom);
    };

    el.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
      el.removeEventListener("scroll", onScroll);
    };
  }, [showScrollHint, isOverflowing]);

  return (
    <section
      className={`relative overflow-hidden rounded-3xl border border-slate-200 shadow-sm ring-1 ring-slate-900/5 bg-white ${
        compact ? `${heightClass} flex flex-col` : ""
      }`}
    >
      {/* Stripe / Ribbon */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-indigo-400 to-fuchsia-400"
      />

      {/* Header */}
      <div className="px-5 md:px-6 pt-4 pb-3 flex items-center justify-between bg-gray-50/60 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
            <FaStar className="h-3.5 w-3.5" />
          </span>
          <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
            Detalhes do produto
          </h3>
        </div>
        <span className="hidden md:inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
          {hasTags ? "Inclui o que retorna" : "Descrição"}
        </span>
      </div>

      {/* Content (scrollável quando compacto) */}
      <div
        ref={scrollRef}
        className={`${compact ? "min-h-0 flex-1 overflow-y-auto" : ""}`}
      >
        <div className="px-3 py-4">
          {/* Descrição */}
          {hasDescription ? (
            <div className="rounded-2xl bg-gradient-to-b from-slate-50 to-white border border-slate-200 p-4 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
              <h4 className="text-sm font-semibold text-slate-800 mb-2">
                Descrição
              </h4>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-slate-700">
                {description.trim()}
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500">
              Sem descrição disponível para este produto.
            </div>
          )}

          {/* Divisor bonito (somente se houver tags) */}
          {hasTags && (
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                O que retorna
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
            </div>
          )}

          {/* Tags */}
          {hasTags && (
            <div className="rounded-2xl bg-white border border-slate-200 p-3 md:p-4">
              <TagCloud tags={tags} max={18} />
            </div>
          )}
        </div>
      </div>

      {/* Footer sutil */}
      <div className="px-5 md:px-6 py-3 bg-slate-50/60 border-t border-slate-200">
        <p className="text-[11px] text-slate-500">
          Dica: utilize os módulos adicionais para aprofundar a análise quando
          necessário.
        </p>
      </div>

      {/* ---- Dica de scroll ANIMADA (apenas quando compacto, com overflow) ---- */}
      {compact && isOverflowing && !atBottom && hintVisible && (
        <>
          {/* Gradiente na base para indicar conteúdo além do recorte */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-10 h-10 bg-gradient-to-t from-white to-transparent"
          />
          {/* Chevrons + texto com animação */}
          <div className="pointer-events-none absolute inset-x-0 bottom-10 z-10 flex items-center justify-center gap-1">
            <span className="block items-center flex text-[11px] font-medium text-slate-500/80 px-2 py-0.5 rounded-full bg-gray-100 border border-slate-200 backdrop-blur-sm shadow-sm">
              <span>Role para ver mais detalhes</span>
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 text-slate-500/80 animate-bounce"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 16a1 1 0 0 1-.707-.293l-4-4 1.414-1.414L12 13.586l3.293-3.293 1.414 1.414-4 4A1 1 0 0 1 12 16Z"
                />
              </svg>
            </span>
          </div>
        </>
      )}
    </section>
  );
}

/* ===== Adicionais — layout "Rail" com SWITCH + modal de descrição ===== */
function CompactAddonsPanel({
  additionalOptions = [],
  selectedSet: externalSelectedSet,
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
  selectedCount = 0,
}) {
  const selectedSet = useMemo(() => {
    if (externalSelectedSet && typeof externalSelectedSet.has === "function")
      return externalSelectedSet;
    return new Set((selectedAdditionalIds || []).map(String));
  }, [externalSelectedSet, selectedAdditionalIds]);

  const items = useMemo(
    () =>
      (additionalOptions || []).map((opt) => ({
        id: opt?.id,
        name: opt?.name || "",
        description: opt?.description || "",
        longDescription: opt?.meta?.longDescription || opt?.description || "",
        price: opt?.price,
        disabled: Boolean(opt?.disabled),
        tone: opt?.meta?.tone || "default", // "default" | "pro" | "alerta"
      })),
    [additionalOptions]
  );

  const selectedTotal = useMemo(() => {
    let sum = 0;
    for (const o of items) {
      if (selectedSet.has(String(o.id)) && Number.isFinite(Number(o.price)))
        sum += Number(o.price);
    }
    return sum;
  }, [items, selectedSet]);

  const fmtBRL = (v) =>
    Number.isFinite(Number(v))
      ? Number(v).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
      : "";

  const toggle = (id) => {
    const idStr = String(id);
    const current = new Set((selectedAdditionalIds || []).map(String));
    if (current.has(idStr)) current.delete(idStr);
    else current.add(idStr);
    onChangeAdditionalIds(Array.from(current));
  };

  const [modal, setModal] = useState({ open: false, title: "", text: "" });

  return (
    <section className="h-[240px] flex flex-col rounded-3xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-900/5 overflow-hidden">
      {/* Header fixo */}
      <div className="px-4 pt-2 pb-2 flex items-center justify-between shrink-0 bg-gray-50">
        <h3 className="text-[11px] uppercase tracking-[0.18em] text-slate-600 font-semibold flex items-center gap-2">
          <FaStar />
          <span>Incremente sua consulta</span>
        </h3>
        <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700">
          {selectedCount}/{items.length}
          <FaCheckCircle className="text-emerald-500" />
        </span>
      </div>

      {/* Lista scrollável */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2 py-2">
        <ul className="flex flex-col gap-1.5">
          {items.length === 0 && (
            <li className="overflow-hidden text-sm text-slate-500 border border-slate-200 rounded-xl p-3 text-center">
              Nenhum adicional disponível.
            </li>
          )}

          {items.map((opt) => {
            const checked = selectedSet.has(String(opt.id));
            const disabled = Boolean(opt.disabled);

            const toneBorder =
              opt.tone === "pro"
                ? "border-indigo-200 hover:border-indigo-300"
                : opt.tone === "alerta"
                ? "border-amber-200 hover:border-amber-300"
                : "border-slate-200 hover:border-emerald-200";

            const openInfo = (e) => {
              e.stopPropagation();
              setModal({
                open: true,
                title: opt.name,
                text: opt.longDescription || "Sem descrição disponível.",
              });
            };

            return (
              <li key={opt.id}>
                <div
                  className={`group overflow-hidden relative grid grid-cols-[auto,1fr,auto] items-center gap-3 rounded-xl border ${toneBorder} px-3 py-2 transition
                    ${
                      checked
                        ? "bg-emerald-50/60 ring-1 ring-emerald-200"
                        : "bg-white hover:bg-emerald-50/30"
                    }
                    ${
                      disabled
                        ? "opacity-60 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                  title={opt.description || opt.name}
                  onClick={() => !disabled && toggle(opt.id)}
                >
                  {/* acento fino */}
                  <span
                    aria-hidden
                    className={`absolute left-0 top-0 h-full w-[3px] rounded-l-xl transition
                      ${
                        checked
                          ? "bg-emerald-400"
                          : "bg-slate-200 group-hover:bg-emerald-200"
                      }`}
                  />

                  {/* SWITCH */}
                  <UISwitch
                    checked={checked}
                    disabled={disabled}
                    onChange={() => !disabled && toggle(opt.id)}
                    ariaLabel={`Ativar adicional ${opt.name}`}
                  />

                  {/* texto + botão de info */}
                  <div className="min-w-0 flex items-center">
                    <div className="flex items-center gap-1 min-w-0">
                      <p
                        className={`truncate text-[13px] font-semibold ${
                          checked ? "text-emerald-800" : "text-slate-800"
                        }`}
                      >
                        {opt.name}
                      </p>

                      <FaQuestionCircle
                        className="text-gray-400"
                        fontSize={13}
                        onClick={openInfo}
                      />
                    </div>
                  </div>

                  {/* preço */}
                  {Number.isFinite(Number(opt.price)) && (
                    <span
                      className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold
                        ${
                          checked
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-50 text-slate-700"
                        }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {fmtBRL(opt.price)}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Rodapé fixo com resumo */}
      <div className="shrink-0 border-t border-slate-200 bg-slate-50/70 px-4 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-slate-700">
            Selecionados:{" "}
            <strong className="font-semibold">{selectedCount}</strong>
          </span>
          <span className="text-[12px] text-slate-700">
            Total:{" "}
            <strong className="font-semibold">{fmtBRL(selectedTotal)}</strong>
          </span>
        </div>
      </div>

      {/* MODAL de descrição */}
      <InfoModal
        open={modal.open}
        title={modal.title}
        text={modal.text}
        onClose={() => setModal({ open: false, title: "", text: "" })}
      />
    </section>
  );
}

/* ---- Modal simples, acessível ---- */
function InfoModal({ open, title, text, onClose }) {
  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === "Escape") onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      aria-modal="true"
      role="dialog"
      onKeyDown={onKeyDown}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* content */}
      <div className="relative z-[61] w-[92vw] max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/10">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-slate-200">
          <h4 className="text-sm font-semibold text-slate-900 truncate">
            {title}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
            aria-label="Fechar"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path
                fill="currentColor"
                d="M6.225 4.811 4.81 6.225 10.586 12l-5.775 5.775 1.414 1.414L12 13.414l5.775 5.775 1.414-1.414L13.414 12l5.775-5.775-1.414-1.414L12 10.586 6.225 4.81Z"
              />
            </svg>
          </button>
        </div>
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
            {text || "Sem descrição disponível."}
          </p>
        </div>
        <div className="p-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Switch reutilizável (acessível) ---- */
function UISwitch({ checked, disabled, onChange, ariaLabel }) {
  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onChange?.();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.();
      }}
      onKeyDown={onKeyDown}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition
        ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
        ${checked ? "bg-emerald-500" : "bg-slate-300"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
          ${checked ? "translate-x-4" : "translate-x-1"}`}
      />
    </button>
  );
}

/* ===== Nuvem de Tags compacta ===== */
function TagCloud({ tags = [], max = 12 }) {
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(() => {
    const arr = (Array.isArray(tags) ? tags : []).map((t) =>
      typeof t === "string" ? { name: t } : t || {}
    );
    const seen = new Set();
    const unique = [];
    for (const t of arr) {
      const key = String(t.name ?? "")
        .trim()
        .toLocaleLowerCase("pt-BR");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(t);
    }
    return unique.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt-BR", {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [tags]);

  const visible = expanded ? sorted : sorted.slice(0, max);
  const hidden = Math.max(sorted.length - visible.length, 0);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ul className="flex flex-wrap gap-2">
        {visible.map((item, i) => (
          <li key={i}>
            <span
              title={item.name}
              className="inline-flex max-w-full items-center gap-2 truncate
                         rounded-2xl border border-slate-200 bg-white/80 px-3 py-1.5 text-[12px] text-slate-700
                         shadow-sm ring-1 ring-white/50 backdrop-blur transition hover:border-emerald-200 hover:shadow"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
              <span className="truncate">{item.name}</span>
            </span>
          </li>
        ))}
      </ul>

      {hidden > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50
                     px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:border-emerald-200 hover:bg-white"
        >
          +{hidden}
        </button>
      )}
      {expanded && sorted.length > max && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50
                     px-3 py-1.5 text-[12px] font-medium text-slate-700 hover:border-emerald-200 hover:bg-white"
        >
          Mostrar menos
        </button>
      )}
    </div>
  );
}
